// Lightweight passive network status store. Fed by SyncEngine + chatHub via
// recordSuccess() / recordFailure(), plus navigator.onLine window events.
// No active ping — we piggy-back on the sync delta poll as a natural heartbeat.

export type NetworkStatus = 'online' | 'degraded' | 'offline';
type Listener = (status: NetworkStatus) => void;

class NetworkStatusStore {
  private _status: NetworkStatus = 'online';
  private failures = 0;
  private listeners = new Set<Listener>();

  constructor() {
    if (typeof window === 'undefined') return;
    if (!navigator.onLine) this._status = 'offline';
    window.addEventListener('online', this.handleBrowserOnline);
    window.addEventListener('offline', this.handleBrowserOffline);
  }

  private handleBrowserOnline = () => {
    this.failures = 0;
    this.set('online');
  };

  private handleBrowserOffline = () => {
    this.set('offline');
  };

  get status(): NetworkStatus {
    return this._status;
  }

  recordSuccess(): void {
    this.failures = 0;
    if (this._status !== 'online') this.set('online');
  }

  recordFailure(): void {
    this.failures++;
    if (this.failures >= 4) this.set('offline');
    else if (this.failures >= 2) this.set('degraded');
  }

  subscribe(fn: Listener): () => void {
    this.listeners.add(fn);
    return () => {
      this.listeners.delete(fn);
    };
  }

  private set(s: NetworkStatus): void {
    if (this._status === s) return;
    this._status = s;
    this.listeners.forEach(l => l(s));
  }
}

export const networkStatus = new NetworkStatusStore();
